// Helpers for the widget bootstrap path. Kept separate from widget.ts so they
// can be unit-tested without triggering BookingWidget initialization.

// Derive apiBase from the widget script's own URL so that external embeds can
// drop in a single <script> tag without also having to set window.BookingWidget.
// Returns undefined if the URL doesn't look like a widget script (caller falls
// back to whatever apiBase is already configured).
export function deriveApiBaseFromScriptUrl(scriptUrl: string | undefined): string | undefined {
  if (!scriptUrl) return undefined;
  try {
    const u = new URL(scriptUrl);
    const path = u.pathname;
    // Match a trailing /portal.js (optionally preceded by /static or /widget).
    const m = path.match(/^(.*?)\/(?:static\/|widget\/)?portal\.js$/);
    if (!m) return undefined;
    const basePath = m[1]; // may be empty if served at root
    return `${u.origin}${basePath}`;
  } catch {
    return undefined;
  }
}

export function findWidgetScriptSrc(): string | undefined {
  if (typeof document === 'undefined') return undefined;
  const current = document.currentScript as HTMLScriptElement | null;
  if (current?.src) return current.src;
  // Fallback for deferred/async loads where currentScript is null at execution.
  const all = Array.from(document.scripts || []) as HTMLScriptElement[];
  const match = all.find(s => /\/portal\.js(?:\?|#|$)/.test(s.src));
  return match?.src;
}


// URL of the on-demand quick-book bundle. It sits next to portal.js: for the
// static path that is /static/quick-book.js; for the tenant-scoped
// /<tenant>/widget.js it is served by the same host under /static/.
export function quickBookChunkUrl(scriptUrl: string | undefined, apiBase?: string): string | undefined {
  return chunkUrl('quick-book.js', scriptUrl, apiBase);
}

// Same for the on-demand retail catalog bundle (dist/catalog.js).
export function catalogChunkUrl(scriptUrl: string | undefined, apiBase?: string): string | undefined {
  return chunkUrl('catalog.js', scriptUrl, apiBase);
}

// Same for the on-demand public price list bundle (dist/price-list.js).
export function priceListChunkUrl(scriptUrl: string | undefined, apiBase?: string): string | undefined {
  return chunkUrl('price-list.js', scriptUrl, apiBase);
}

function chunkUrl(file: string, scriptUrl: string | undefined, apiBase?: string): string | undefined {
  if (scriptUrl) {
    try {
      const u = new URL(scriptUrl);
      if (/\/portal\.js(?:\?|#|$)/.test(u.pathname)) return `${u.origin}${u.pathname.replace(/portal\.js$/, file)}`;
      if (/\/widget\.js(?:\?|#|$)/.test(u.pathname)) return `${u.origin}/static/${file}`;
    } catch { /* fall through */ }
  }
  if (apiBase) {
    try { return `${new URL(apiBase).origin}/static/${file}`; } catch { /* ignore */ }
  }
  return undefined;
}
